import React, { useState } from 'react';
import {
    Box,
    Container,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { ThemeProvider } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import DainTheme from '../DainTheme';
import { useGetMediaSummary } from '../api/MonitoringApi';
import RoutePaths from '../routes/RoutePaths';
import MediaSummaryTable from './MediaSummaryTable';

export default function Monitoring() {
    const intl = useIntl();
    const navigate = useNavigate();
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const mediaSummaryQuery = useGetMediaSummary({});

    const isMenuOpen = Boolean(menuAnchorEl);

    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleNavigate = (path) => {
        handleMenuClose();
        navigate(path);
    };

    const medias = mediaSummaryQuery?.data?.result ?? [];

    return (
        <ThemeProvider theme={DainTheme.light}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        flex: '0 0 auto',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1,
                    }}
                >
                    <Container maxWidth="md">
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ position: 'relative', minHeight: 48 }}
                        >
                            <Stack alignItems="center" spacing={0.5}>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, color: 'primary.main', fontFamily: 'monospace' }}
                                >
                                    {intl.formatMessage({ id: 'monitoring.title' })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {intl.formatMessage({ id: 'monitoring.subtitle' })}
                                </Typography>
                            </Stack>
                            <IconButton
                                aria-label={intl.formatMessage({ id: 'monitoring.nav.menu' })}
                                aria-controls={isMenuOpen ? 'monitoring-nav-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={isMenuOpen ? 'true' : undefined}
                                onClick={handleMenuOpen}
                                sx={{ position: 'absolute', right: 0 }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                id="monitoring-nav-menu"
                                anchorEl={menuAnchorEl}
                                open={isMenuOpen}
                                onClose={handleMenuClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem
                                    onClick={() => handleNavigate(RoutePaths.monitoring.allSources)}
                                >
                                    {intl.formatMessage({ id: 'monitoring.column.viewAllSources' })}
                                </MenuItem>
                                <MenuItem
                                    onClick={() => handleNavigate(RoutePaths.monitoring.alerts)}
                                >
                                    {intl.formatMessage({ id: 'monitoring.column.viewAlerts' })}
                                </MenuItem>
                            </Menu>
                        </Stack>
                    </Container>
                </Box>

                <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Container
                        maxWidth="md"
                        sx={{
                            py: 2,
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {mediaSummaryQuery.isLoading && (
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.loading' })}
                            </Typography>
                        )}
                        {mediaSummaryQuery.isError && (
                            <Typography variant="body2" color="error" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.error' })}
                            </Typography>
                        )}
                        {!mediaSummaryQuery.isLoading &&
                            !mediaSummaryQuery.isError &&
                            medias.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                    {intl.formatMessage({ id: 'monitoring.empty' })}
                                </Typography>
                            )}
                        {medias.length > 0 && (
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                <MediaSummaryTable medias={medias} />
                            </Box>
                        )}
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
